import type {
  Dispatch,
  MapDispatchToProps,
  MapStateToProps,
  ThunkAction,
} from 'types/redux';

import { ComponentType } from '@wix/ambassador-devcenter-v1-component/types';
import type { Callback } from '@wix/document-services-types';

import * as util from '#packages/util';
import type { EditorAPI } from '#packages/editorAPI';
import { deeplink } from '#packages/rEditor';
import { appMarketHandlers, services } from '#packages/tpa';
import constants from '#packages/constants';
import { panels, platform } from '#packages/stateManagement';
import * as platformEvents from 'platformEvents';
import experiment from 'experiment';

import type {
  AppManagerDispatchProps,
  AppManagerStateProps,
  AppMarketAddAppParams,
} from '@wix/editor-platform-host-integration/ui';
import type { GroupPermissions } from '@wix/platform-editor-sdk';
import {
  EditorType,
  InstallationOriginType,
  InstallInitiator,
} from '@wix/platform-editor-sdk';
import { FEATURE } from '@wix/deeplink-core';

import experiments from 'experiment';

import { APP_MANAGER_ORIGIN } from './constants';
import { getAppsList } from './utils';
import { EditorPlatformHostIntegrationAPI } from '@wix/editor-platform-host-integration-apis';

const { getPremiumUrl, getPremiumUrlForSiteUpgrade } = util.premiumUtils;

export const mapStateToProps: MapStateToProps<AppManagerStateProps> = ({
  editorAPI,
}) => {
  const userPermissions = new Set(editorAPI.generalInfo.getUserPermissions());

  const pendingAppDefIds: AppManagerStateProps['pendingAppDefIds'] =
    services.pendingAppsService
      .getAllPendingAppDefIds(editorAPI)
      .reduce((acc, appId) => ({ ...acc, [appId]: true }), {});

  return {
    locale: util.editorModel.languageCode,
    appsList: getAppsList(editorAPI),
    experiments: experiments.getRunningExperiments(),
    userPermissions,
    pendingAppDefIds,
    allAvailableToUpdateApps: [],
  };
};

const getEditorAPI: ThunkAction<EditorAPI> = (
  dispatch,
  getState,
  { editorAPI },
) => editorAPI;

export const mapDispatchToProps: MapDispatchToProps<
  AppManagerDispatchProps,
  {}
> = (dispatch: Dispatch) => {
  const editorAPI: EditorAPI = dispatch(getEditorAPI);
  const hostIntegrationAPI = editorAPI.host.getAPI(
    EditorPlatformHostIntegrationAPI,
  );

  const metaSiteInstance = editorAPI.dsRead.platform.getAppDataByApplicationId(
    constants.APPLICATIONS.META_SITE_APPLICATION_ID,
  ).instance;

  const addApp = async (options: AppMarketAddAppParams) => {
    await appMarketHandlers.addApp(editorAPI, {
      appDefId: options.appDefId,
      biData: {
        origin: APP_MANAGER_ORIGIN,
      },
      platformOrigin: {
        initiator: InstallInitiator.Editor,
        type: EditorType.Classic,
        info: {
          type: InstallationOriginType.MY_BUSINESS,
        },
      },
      appVersion: options.appVersion,
    });
  };

  const openAppMarket = (openMarketOrigin: string) =>
    dispatch(
      panels.actions.openLeftPanel(
        constants.ROOT_COMPS.LEFTBAR.ADD_APPS_PANEL_NAME,
        {
          urlParams: {
            openMarketOrigin,
          },
        },
      ),
    );

  const onUpdateApp = (appDefId: string, appVersion: string) => {
    appMarketHandlers.updateApp(editorAPI, {
      params: {
        appDefinitionId: appDefId,
        appVersion,
        platformOrigin: {
          initiator: InstallInitiator.Editor,
          type: EditorType.Classic,
          info: {
            type: InstallationOriginType.MY_BUSINESS,
          },
        },
      },
    });
  };

  const onUninstallApp = (
    appDefId: string,
    shouldShowConfirmationModal: boolean,
  ) => {
    const appData = editorAPI.platform.getAppDataByAppDefId(appDefId);
    editorAPI.platform.applications.uninstall(
      appData,
      shouldShowConfirmationModal,
    );
  };

  const openHelpCenter = (helpId: string) =>
    editorAPI.panelManager.openHelpCenter(helpId);

  const openBusinessManager = (appDefId: string, path: string) => {
    return editorAPI.platform.panels.openDashboardPanel(
      editorAPI.dsRead.platform.getAppDataByAppDefId(appDefId),
      { url: path },
    );
  };

  const openPagesPanel = (appDefId: string) =>
    editorAPI.panelHelpers.openPagesPanel({
      selectedTabId: appDefId,
    });

  const openAddPanel = (appDefId: string) =>
    deeplink.show({
      type: FEATURE.addPanel,
      params: [appDefId],
    });

  const openDashboardPanel = (appDefId: string) => {
    return editorAPI.platform.panels.openDashboardPanel(
      editorAPI.dsRead.platform.getAppDataByAppDefId(appDefId),
      { url: `app/${appDefId}` },
    );
  };

  const notifyApplication = (appDefId: string, actionId: string) => {
    const applicationId =
      editorAPI.dsRead.platform.getAppDataByAppDefId(appDefId)?.applicationId;
    editorAPI.platform.notifyApplication(
      applicationId,
      platformEvents.factory.appActionClicked({
        actionId,
      }),
    );
  };

  const addGrantedToPermissions = (permissions?: {
    groups?: GroupPermissions[];
    custom?: string[];
  }) => editorAPI.platform.permissions.addGrantedToPermissions(permissions);

  const getSitePremiumUrl = (appDefId: string, _origin?: string) => {
    const premiumOrigin = `${APP_MANAGER_ORIGIN}_${appDefId}`;
    return getPremiumUrlForSiteUpgrade(editorAPI, {
      appDefinitionId: appDefId,
      origin: premiumOrigin,
      referralAdditionalInfo: premiumOrigin,
    });
  };

  const getAppPremiumUrl = (
    appDefId: string,
    origin?: string,
    referralAdditionalInfo?: string,
  ) => {
    return getPremiumUrl(editorAPI, {
      appDefinitionId: appDefId,
      origin,
      referralAdditionalInfo,
    });
  };

  const appHasPages = (appDefId: string) =>
    deeplink.check({
      type: FEATURE.pagesPanel,
      params: [appDefId],
    });

  const appHasWidgets = (appDefId: string) =>
    deeplink.check({
      type: FEATURE.addPanel,
      params: [appDefId],
    });

  const appHasDashboard = (appDefId: string) => {
    const appComponents =
      editorAPI.dsRead.platform.getAppDataByAppDefId(appDefId)?.components;
    return (
      appComponents?.some(
        (component) => component.type === ComponentType.BACK_OFFICE_PAGE,
      ) ?? false
    );
  };

  const checkAppsCompletion = (callback: Callback) => {
    if (
      platform.selectors.hasEditorScriptsLoadedReported(
        editorAPI.store.getState(),
      )
    ) {
      return true;
    }
    editorAPI.dsActions.platform.registerToAppsCompleted(callback);
  };

  const appHasPremiumOffering = (applicationId: number): boolean => {
    if (experiment.isOpen('se_appDefIdUpdates')) {
      const { appDefId } =
        editorAPI.platform.getAppDataByApplicationId(applicationId);
      return editorAPI.dsRead.tpaV2.app.hasPremiumOffering(appDefId);
    }

    return editorAPI.dsRead.tpa.app.hasPremiumOffering(applicationId);
  };

  return {
    apiDependencies: {
      isClassicEditor: true,
      metaSiteInstance,
      addApp,
      openAppMarket,
      onUpdateApp,
      onUninstallApp,
      isAppUpdateAvailable:
        hostIntegrationAPI.applications.isAppUpdateAvailable,
      getAppDataByAppDefId: editorAPI.platform.getAppDataByAppDefId,
      getAppDescriptor: editorAPI.dsRead.platform.getAppDescriptor,
      getInstalledEditorApps: editorAPI.dsRead.platform.getInstalledEditorApps,
      openSettings: editorAPI.account.openSettings,
      openHelpCenter,
      openBusinessManager,
      openPagesPanel,
      openAddPanel,
      openDashboardPanel,
      notifyApplication,
      addGrantedToPermissions,
      isPremium: editorAPI.site.isPremium,
      getSitePremiumUrl,
      getAppPremiumUrl,
      appHasPremiumOffering,
      appHasPages,
      appHasWidgets,
      appHasDashboard,
      checkAppsCompletion,
    },
  };
};
